import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Users,
  Layout,
  Code,
  Shield,
  Globe,
  PieChart,
} from "lucide-react";

const ProcessSection = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

const whatWeOffer = [
  {
    icon: Globe,
    title: "End-to-End Recruitment Support",
    description:
      "Complete hiring lifecycle management—from job posting and sourcing to screening, selection, and onboarding the right talent.",
  },
  {
    icon: Layout,
    title: "Payroll Processing & Compliance",
    description:
      "Accurate and timely payroll execution, including allowances, deductions, PF, ESI, TDS, tax filings, and statutory compliance.",
  },
  {
    icon: Users,
    title: "Employee Database Management",
    description:
      "Centralized employee records with automated tracking for attendance, leave, documentation, and HR operations.",
  },
  {
    icon: PieChart,
    title: "Salary Structuring & Benefits Management",
    description:
      "Optimized salary structures with complete management of incentives, reimbursements, arrears, and employee benefits.",
  },
  {
    icon: Code,
    title: "HR Automation & Digital Tools",
    description:
      "Smart digital HR solutions for payroll, attendance, onboarding, and performance—reducing paperwork and manual errors.",
  },
  {
    icon: Shield,
    title: "Secure & Confidential Data Handling",
    description:
      "Enterprise-grade security ensuring confidential employee information is protected and fully compliant with data policies.",
  },
];


const processSteps = [
  {
    number: "01",
    title: "Talent Understanding & Requirement Planning",
    description:
      "We understand your hiring needs, job roles, organizational structure, and payroll frameworks to build a tailored HR strategy.",
    icon: Users,
      imagePath: "./images/servicesImages/Talent Understanding & Requirement Planning.png",
  },
  {
    number: "02",
    title: "Sourcing, Screening & Onboarding",
    description:
      "We identify candidates, conduct evaluations, coordinate interviews, and assist with complete onboarding documentation.",
    icon: Layout,
      imagePath: "./images/servicesImages/Sourcing, Screening & Onboarding.png",
  },
  {
    number: "03",
    title: "Payroll Setup & Monthly Processing",
    description:
      "Payroll cycles are configured, calculations automated, and monthly salary processing is executed with precision and accuracy.",
    icon: Code,
      imagePath: "./images/servicesImages/Payroll Setup & Monthly Processing.png",
  },
  {
    number: "04",
    title: "Compliance, Reporting & Ongoing Support",
    description:
      "Tax filings, statutory compliance, employee reports, and HR support are handled continuously to ensure smooth operations.",
    icon: Shield,
      imagePath: "./images/servicesImages/Compliance, Reporting & Ongoing Support.png",
  },
];


const ourEdge = [
  "👨‍💼 Expertise Across Industries: Scalable HR, hiring, and payroll solutions for startups, SMEs, and enterprises.",
  "⚖️ 100% Compliance Assured: PF, ESI, TDS, labor laws, and statutory requirements handled with complete accuracy.",
  "⚙️ Automated HR Tools: Reduce manual workload with automated workflows for attendance, payroll, and onboarding.",
  "🔒 Trusted & Secure Processes: Enterprise-level data protection with strict confidentiality and secure access controls.",
];


const howWeWork = [
  "1. Requirement Discovery – We evaluate your hiring needs, workforce structure, and payroll expectations.",
  "2. Recruitment & Documentation – Candidates are sourced, screened, interviewed, and onboarded with complete documentation.",
  "3. Payroll Execution – Monthly payroll is processed with attendance data, deductions, incentives, taxes, and statutory components.",
  "4. Compliance & HR Support – We manage PF, ESI, TDS filings, maintain employee records, and provide continuous HR assistance.",
];


  return (
    <section className="py-20 px-[5%] bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* What We Offer Section */}
        <div className="mb-28">
          <h2
            className="text-center text-3xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-12"
            data-aos="fade-up"
          >
            What We Offer
          </h2>
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {whatWeOffer.map((feature, index) => (
              <div
                key={index}
                className="bg-[#dcfaf9] rounded-full p-8 shadow-md border-t-4 border-b-4 border-[#0d6b68] hover:shadow-xl transition-all duration-300"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center gap-4 mb-4">
                  <feature.icon className="w-8 h-8 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-32" data-aos="fade-up">
  <h2 className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-6">
    Our Process
  </h2>

  <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
    We follow a structured, end-to-end approach to streamline hiring, payroll,
    and HR operations. From understanding your staffing needs to onboarding,
    payroll setup, compliance, and ongoing HR support — every step is designed
    for accuracy, transparency, and smooth workforce management.
  </p>
</div>


        {/* Process Grid */}
        <div className="grid md:grid-cols-2 gap-y-32 gap-x-10">
          {processSteps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div
                key={index}
                className="flex flex-col relative"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                {/* Background Number */}
                <div className="absolute -top-24 text-6xl md:text-9xl font-bold text-[#9ee8e7] select-none pointer-events-none z-0">
                  {step.number}
                </div>

                {/* Card */}
                <div className="bg-gradient-to-br from-[#0d6b68] to-[#baf7f6] rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 z-10 border border-gray-100">
                  {/* Card Image/Icon Area */}
                  <div className="mb-8 relative">
                    <div className="w-full h-fit bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl flex items-center justify-center overflow-hidden relative">
                      <img
                        src={step.imagePath}
                        alt={step.title}
                        className="w-full h-38 md:h-40 lg:h-52 object-cover rounded-2xl"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />

                      {/* Fallback Icon */}
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <div className="relative">
                          <div className="absolute -top-4 -left-4 w-3 h-3 bg-blue-400 rounded-full opacity-60"></div>
                          <div className="absolute -bottom-6 -right-6 w-2 h-2 bg-purple-400 rounded-full opacity-40"></div>
                          <div className="absolute top-2 right-4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-50"></div>
                          <div className="bg-white p-6 rounded-2xl shadow-lg">
                            <IconComponent className="w-12 h-12 text-blue-600" />
                          </div>
                          <div className="absolute top-1/2 -left-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute top-1/2 -right-8 w-6 h-0.5 bg-blue-300 opacity-60"></div>
                          <div className="absolute -top-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                          <div className="absolute -bottom-8 left-1/2 w-0.5 h-6 bg-blue-300 opacity-60"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Our Edge Section */}
        <div className="text-center mt-32" data-aos="fade-up">
          <h2
            className="text-4xl md:text-6xl font-semibold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-8"
            style={{ lineHeight: "1.4" }}
          >
            Our Edge
          </h2>

          <ul className="text-gray-700 max-w-4xl mx-auto space-y-4 text-lg">
            {ourEdge.map((point, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {point}
              </li>
            ))}
          </ul>
        </div>

        {/* How We Work Section */}
        <div className="text-center mt-20" data-aos="fade-up">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-8 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            How We Work
          </h2>
          <ul className="text-gray-700 max-w-3xl mx-auto space-y-4 text-lg">
            {howWeWork.map((step, index) => (
              <li key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                {step}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
